import { Component, OnInit, ViewChild } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { CostService } from 'src/app/shared/services/cost.service';
import {map, tap, takeUntil, filter, switchMap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, forkJoin} from 'rxjs';
import { CostStatuses, Cost } from 'src/app/shared/models/cost.model';
import { TableDataSource } from 'src/app/shared/components/table/table-data-source';
import { MatSort } from '@angular/material/sort';
import { tableSortFn } from 'src/app/shared/utils/sort.util';
import { SelectionModel } from '@angular/cdk/collections';
import {ToastrService} from 'ngx-toastr';
import {ConfirmService} from '../../../shared/services/confirm.service';
import {Redirect} from '../../../shared/utils/redirect.util';
import { CostHistory } from 'src/app/shared/models/cost-history.model';
import { AuthService } from 'src/app/auth/auth.service';
import { UserService } from 'src/app/shared/services/user.service';

interface CostWithHistory extends Cost {
  history: CostHistory[];
}

@Component({
  selector: 'app-advances-costs',
  templateUrl: './advances-costs.component.html'
})
export class AdvancesCostsComponent implements OnInit {
  costStatus = CostStatuses;
  costStatusTab: string;
  costsByTab$;
  costs$;
  costsCount$;
  costHistory: any;
  routeParams$;
  costHistoryLoaded : boolean = false;
  loggedInEmployeeId: number | null = null;

  dataSource = new TableDataSource<Cost>();
  selection = new SelectionModel<Cost>(true, []);
  displayedColumns: string[] = ['name', 'status.name', 'advance.name', 'grossValue', 'createdAt', 'updatedAt', 'action'];

  @ViewChild(MatSort, { static: false }) set sort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor(
    private route: ActivatedRoute,
    private costService: CostService,
    private toastrService: ToastrService,
    private confirmService: ConfirmService,
    private redirect: Redirect,
    private authService: AuthService,
    private userService: UserService,
  ) { }

  ngOnInit() {
    this.costs$ = this.costService.fetchAllEmployeeCosts();
    this.routeParams$ = this.route.params.subscribe(({ costStatus }) => {
      this.costHistoryLoaded = false; // Ustawienie flagi na true
    });
    
    // Get logged-in employee ID for filtering search results
    // Use the same method as fetchAllEmployeeCosts to ensure consistency
    try {
      const currentUser = this.userService.getCurrentUser();
      if (currentUser && currentUser.id) {
        this.loggedInEmployeeId = currentUser.id;
      }
    } catch (e) {
      // Fallback to authEmployee$ if getCurrentUser fails
      this.authService.authEmployee$.subscribe((employee) => {
        if (employee && employee.id) {
          this.loggedInEmployeeId = employee.id;
        }
      });
    }
    this.costsCount$ = this.costs$.pipe(
      map((costs: Cost[]) => costs.reduce((totals, cost) => ({
        ...totals, [cost.status.name]: (totals[cost.status.name] || 0) + 1
      }), {})
      ),
      map(tabs => {
        Object.values(CostStatuses).map(status => {
          tabs[status] = tabs[status] || 0;
        });
        tabs['progress'] =
          tabs[CostStatuses.New]
          + tabs[CostStatuses.InApproval]
          + tabs[CostStatuses.InMeritoricalApproval]
          + tabs[CostStatuses.InFormalApproval]
          + tabs[CostStatuses.ReadyToReturn]
          + tabs[CostStatuses.Returned]
          + tabs[CostStatuses.Approved]
          + tabs[CostStatuses.Exported]
          + tabs[CostStatuses.Printed];
        return tabs;
      }),
    );

    this.costsByTab$ = combineLatest([
      this.route.paramMap,
      this.costs$
    ])
      .pipe(
        map(([paramsAsMap, costs]) => (<Cost[]>costs).filter(cost => {
          const costStatusTab = paramsAsMap.get('costStatus');
          let hasStatus = false;
          costStatusTab === 'progress'
            ? hasStatus = cost.status.name !== CostStatuses.Settled
            && cost.status.name !== CostStatuses.Rejected
            && cost.status.name !== CostStatuses.Canceled
            : hasStatus = cost.status.name === costStatusTab;
          return hasStatus;
        })),
        // map((costs: Cost[]) => {
        //   this.totalItems = costs.length;
        //   this.totalPages = Math.ceil(this.totalItems / this.pageSize);
  
        //   // Calculate start and end index of the current page
        //   const startIndex = (this.currentPage) * this.pageSize;
        //   const endIndex = startIndex + this.pageSize;
  
        //   // Slice the costs array based on the current page
        //   const slicedCosts = costs.slice(startIndex, endIndex);
  
        //   this.dataSource.data = slicedCosts;
        //   this.dataSource.sortingDataAccessor = tableSortFn;
        //   this.costHistoryLoaded = true; // Set the flag to true
        //   return slicedCosts;
        // }),
        // switchMap((costs: CostWithHistory[]) => {
        //   const fetchHistoryObservables = costs.map(cost => this.fetchHistory(cost.id));
        //   return forkJoin(fetchHistoryObservables).pipe(
        //     map(histories => {
        //       costs.forEach((cost, index) => {
        //         cost.history = histories[index];
        //       });
        //       return costs;
        //     })
        //   );
        // }),
        map((costs: CostWithHistory[]) => {
          this.dataSource.data = costs;
          this.dataSource.sortingDataAccessor = tableSortFn;
          this.costHistoryLoaded = true; // Ustawienie flagi na true
          return costs;
        }),
      );

  }

  //   fetchHistory(costId) {
  //   return this.costService.fetchHistory(costId).pipe(
  //     tap((costHistory: any) => {
  //       const costIndex = this.dataSource.data.findIndex(cost => cost.id === costId);
  //       if (costIndex !== -1) {
  //         const costWithHistory = this.dataSource.data[costIndex] as CostWithHistory;
  //         costWithHistory.history = costHistory;
  //       }
  //     })
  //   );
  // }

  removeCost(cost: Cost) {
    if (cost.canRemove) {
      this.confirmService.openConfirmDialog('Czy na pewno chcesz usunąć koszt? Zmiana będzie nieodwracalna.')
        .afterClosed().subscribe(confirm => {
        if (confirm) {
          this.costService.removeCost(cost.id).subscribe(res => {
            this.toastrService.success('Koszty został usunięty.');
            this.redirect.to();
          }, _ => this.toastrService.error('Koszt nie może zostać usunięty.'));
        }
      });
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
        this.selection.clear() :
        this.dataSource.data.forEach(row => this.selection.select(row));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Cost): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }
}
